import { coerceBooleanProperty } from '@angular/cdk/coercion'
import { CommonModule } from '@angular/common'
import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    Output,
    ViewChild,
    forwardRef,
} from '@angular/core'
import {
    FormsModule,
    NG_VALUE_ACCESSOR,
    ReactiveFormsModule,
} from '@angular/forms'
import { MatDividerModule } from '@angular/material/divider'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatIconModule } from '@angular/material/icon'
import { MatInputModule } from '@angular/material/input'
import { MatSelect, MatSelectModule } from '@angular/material/select'
import { LoadingModule } from '@loading-lib/loading.module'
import { TranslationModule } from '@localization-lib/language/translation.module'
import { BaseControlValueAccessor } from '@shared-ui-lib/base-control-value-accessor/base-control-value-accessor.component'
import { ButtonModule } from '@shared-ui-lib/button/button.module'
import {
    BehaviorSubject,
    ReplaySubject,
    Subscription,
    combineLatest,
    map,
} from 'rxjs'
import { DropdownOption, DropdownOptionGroup } from './dropdown.model'

const DEFAULT_SEARCH_THRESHOLD = 0

@Component({
    imports: [
        CommonModule,
        ButtonModule,
        FormsModule,
        LoadingModule,
        MatDividerModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatSelectModule,
        ReactiveFormsModule,
        TranslationModule,
    ],
    standalone: true,
    selector: 'app-dropdown',
    templateUrl: './dropdown.component.html',
    styleUrls: ['./dropdown.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            // eslint-disable-next-line @angular-eslint/no-forward-ref
            useExisting: forwardRef(() => DropdownComponent),
            multi: true,
        },
    ],
})
export class DropdownComponent<T>
    extends BaseControlValueAccessor<T>
    implements OnDestroy
{
    appearance: 'outline' | 'fill' = 'outline'
    subscriptions = new Subscription()

    @ViewChild('matSelect') matSelect?: MatSelect

    @Input()
    set flat(value: boolean) {
        if (coerceBooleanProperty(value)) {
            this.appearance = 'fill'
        }
    }

    @Input()
    set stroked(value: boolean) {
        if (coerceBooleanProperty(value)) {
            this.appearance = 'outline'
        }
    }

    _label?: string
    @Input()
    set label(value: string | undefined) {
        this._label = value
    }
    get label() {
        return this._label
    }

    _labelAfterSelection?: string
    @Input()
    set labelAfterSelection(value: string | undefined) {
        this._labelAfterSelection = value
    }
    get labelAfterSelection() {
        return this._labelAfterSelection
    }

    _translateFieldLabel = false
    @Input()
    set translateFieldLabel(value: boolean) {
        this._translateFieldLabel = coerceBooleanProperty(value)
    }
    get translateFieldLabel() {
        return this._translateFieldLabel
    }

    _translateOptionLabels = false
    @Input()
    set translateOptionLabels(value: boolean) {
        this._translateOptionLabels = coerceBooleanProperty(value)
    }
    get translateOptionLabels() {
        return this._translateOptionLabels
    }

    private _loading = false
    @Input()
    set loading(value: boolean) {
        this._loading = coerceBooleanProperty(value)
    }
    get loading() {
        return this._loading
    }

    _multiple = false
    @Input()
    set multiple(value: boolean) {
        this._multiple = coerceBooleanProperty(value)
    }
    get multiple() {
        return this._multiple
    }

    @Output() readonly selectionChanged = new EventEmitter()

    @Output() readonly selectionReseted = new EventEmitter()

    /*----------------
          SEARCH
    -----------------*/
    searchThreshold$ = new BehaviorSubject<number>(DEFAULT_SEARCH_THRESHOLD)
    @Input()
    set searchThreshold(searchThreshold: number | undefined) {
        this.searchThreshold$.next(searchThreshold ?? DEFAULT_SEARCH_THRESHOLD)
    }

    searchValue$ = new BehaviorSubject<string | undefined>(undefined)

    /*----------------
          OPTIONS
    -----------------*/
    optionGroups$ = new ReplaySubject<DropdownOptionGroup<T>[]>()
    @Input()
    set options(value: DropdownOption<T>[]) {
        if (value) {
            this.optionGroups$.next([{ options: value }])
        }
    }
    @Input()
    set optionGroups(value: DropdownOptionGroup<T>[]) {
        this.optionGroups$.next(value)
    }

    showSearch$ = combineLatest([
        this.optionGroups$,
        this.searchThreshold$,
    ]).pipe(
        map(([optionGroups, searchThreshold]) => {
            if (searchThreshold > 0) {
                const optionsCount = optionGroups.reduce(
                    (count, group) => count + group.options.length,
                    0
                )
                return searchThreshold < optionsCount
            }
            return false
        })
    )

    @Input()
    disabledValues: string[] = []

    optionGroupsFiltered$ = combineLatest([
        this.optionGroups$,
        this.searchValue$,
    ]).pipe(
        map(([optionGroups, search]) => {
            if (!search) return optionGroups

            return optionGroups.map((optionGroup) => {
                const regex = new RegExp(search, 'i') // 'i' for case insensitive

                if (optionGroup.group && regex.test(optionGroup.group))
                    return optionGroup

                const filteredOptions = optionGroup.options.filter((option) =>
                    regex.test(option.label)
                )

                if (filteredOptions.length > 0) {
                    return {
                        group: optionGroup.group,
                        options: filteredOptions,
                    }
                }
            })
        })
    )

    constructor() {
        super()
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe()
    }

    getLabelValue(): string {
        if (
            (this.matSelect?.panelOpen || this.hasValue()) &&
            this.labelAfterSelection
        ) {
            return this.labelAfterSelection
        }

        return this.label ?? ''
    }

    hasValue(): boolean {
        return (
            this.value &&
            this.value != '' &&
            !(Array.isArray(this.value) && this.value.length == 0)
        )
    }

    selectionChange(value: T) {
        this.writeValue(value)
        this.onChanged(value)
        this.selectionChanged.emit()
    }

    clearSelect() {
        this.matSelect?.writeValue(null)
        this.writeValue(null as T)
        this.onChanged(null as T)
        this.selectionReseted.emit()
    }
}
